/**
 * DemoRibbon — slim, dismissible letsgetyou.ai conversion ribbon.
 *
 * Rendered app-wide (login/landing + dashboard) above everything else.
 * Fixed 40px bar at the top of the viewport; the app shell offsets the
 * body and the fixed Sidenav / sticky DashboardNavbar so nothing overlaps.
 *
 * Dismiss state is kept in sessionStorage so it stays hidden for the
 * rest of the demo session but returns for the next prospect.
 */

import { FC } from "react";

// @mui material components
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import GlobalStyles from "@mui/material/GlobalStyles";
import CloseIcon from "@mui/icons-material/Close";

export const DEMO_RIBBON_HEIGHT = 40;
export const DEMO_RIBBON_STORAGE_KEY = "lgyDemoRibbonDismissed";

interface DemoRibbonProps {
  visible: boolean;
  onDismiss: () => void;
}

const DemoRibbon: FC<DemoRibbonProps> = ({ visible, onDismiss }) => {
  if (!visible) return null;

  return (
    <>
      {/* Offset the page + sticky dashboard navbar while the ribbon is shown */}
      <GlobalStyles
        styles={{
          body: { paddingTop: `${DEMO_RIBBON_HEIGHT}px` },
          ".MuiAppBar-positionSticky": {
            top: `${DEMO_RIBBON_HEIGHT + 12}px !important`,
          },
        }}
      />
      <Box
        role="complementary"
        aria-label="letsgetyou.ai demo banner"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: `${DEMO_RIBBON_HEIGHT}px`,
          zIndex: 1250, // above Drawer (1200), below modals (1300)
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, sm: 1.5 },
          px: { xs: 1.5, sm: 2.5 },
          background: "linear-gradient(90deg, #0b1220 0%, #101f3a 55%, #14294e 100%)",
          borderBottom: "1px solid rgba(77, 141, 255, 0.28)",
          boxShadow: "0 2px 10px rgba(7, 12, 24, 0.35)",
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        }}
      >
        {/* Wordmark */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: "#4d8dff",
              boxShadow: "0 0 8px rgba(77, 141, 255, 0.9)",
            }}
          />
          <Box
            component="span"
            sx={{
              color: "#fff",
              fontSize: "0.8125rem",
              fontWeight: 700,
              letterSpacing: "0.02em",
              lineHeight: 1,
            }}
          >
            letsgetyou
            <Box component="span" sx={{ color: "#4d8dff" }}>
              .ai
            </Box>
          </Box>
        </Box>

        {/* Divider */}
        <Box
          sx={{
            display: { xs: "none", sm: "block" },
            width: "1px",
            height: 16,
            backgroundColor: "rgba(255, 255, 255, 0.16)",
            flexShrink: 0,
          }}
        />

        {/* Message */}
        <Box
          component="span"
          sx={{
            display: { xs: "none", sm: "block" },
            color: "rgba(255, 255, 255, 0.85)",
            fontSize: "0.8125rem",
            lineHeight: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
        >
          You&apos;re viewing a live{" "}
          <Box component="strong" sx={{ color: "#fff", fontWeight: 600 }}>
            letsgetyou.ai
          </Box>{" "}
          demo
        </Box>

        {/* Micro-line (large screens only) */}
        <Box
          component="span"
          sx={{
            display: { xs: "none", lg: "block" },
            color: "rgba(255, 255, 255, 0.5)",
            fontSize: "0.75rem",
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        >
          Your brand, your data &mdash; shipped in weeks.
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* CTA */}
        <Box
          component="a"
          href="https://letsgetyou.ai/#contact"
          target="_blank"
          rel="noopener"
          sx={{
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            px: 1.5,
            py: 0.5,
            borderRadius: "999px",
            backgroundColor: "#1A73E8",
            color: "#fff",
            fontSize: "0.75rem",
            fontWeight: 600,
            lineHeight: 1.4,
            letterSpacing: "0.01em",
            textDecoration: "none",
            whiteSpace: "nowrap",
            transition: "background-color 150ms ease, transform 150ms ease",
            "&:hover": {
              backgroundColor: "#1662c4",
              transform: "translateY(-1px)",
              color: "#fff",
            },
          }}
        >
          Get this, branded for you&nbsp;&rarr;
        </Box>

        {/* Dismiss */}
        <IconButton
          size="small"
          aria-label="Dismiss demo banner"
          onClick={onDismiss}
          sx={{
            flexShrink: 0,
            color: "rgba(255, 255, 255, 0.6)",
            p: 0.5,
            "&:hover": { color: "#fff", backgroundColor: "rgba(255, 255, 255, 0.08)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </>
  );
};

export default DemoRibbon;
